import type { GetServerSidePropsContext, NextPageContext } from 'next'

import { readLocaleCookie } from './cookie'
import { getMarketLocale } from './market-locale'
import type { LiveLanguageCodes } from './types'

/**
 * Where a session's locale signals come from: a Next.js server context
 * (server-side) or a Window (client-side). Omit on the client to use the
 * global window.
 */
export type LocalePreferenceSource =
  | GetServerSidePropsContext
  | NextPageContext
  | Window

type LocaleHeaders = {
  cookie?: string
  'accept-language'?: string
}

/**
 * Parses an Accept-Language header into an ordered list of language tags,
 * most-preferred first (by quality value).
 */
export function parseAcceptLanguage(header: string | undefined): string[] {
  if (!header) {
    return []
  }

  return (
    header
      .split(',')
      .map((part) => {
        const [tag, ...params] = part.trim().split(';')
        const quality = params
          .map((param) => param.trim())
          .find((param) => param.startsWith('q='))
        const q = quality ? Number.parseFloat(quality.slice(2)) : 1
        return { tag: tag?.trim() ?? '', q: Number.isNaN(q) ? 0 : q }
      })
      // Drop the wildcard and q=0 ("not acceptable", per RFC 7231 §5.3.1).
      .filter((entry) => entry.tag && entry.tag !== '*' && entry.q > 0)
      .sort((a, b) => b.q - a.q)
      .map((entry) => entry.tag)
  )
}

// Dedupe while preserving order: the cookie locale may also appear in the
// browser/header signals. getMarketLocale short-circuits on the first match
// either way, but a clean list keeps the contract obvious.
const dedupe = (tags: string[]): string[] => [...new Set(tags.filter(Boolean))]

const fromHeaders = (headers: LocaleHeaders | undefined): string[] => {
  const cookieLocale = readLocaleCookie(headers?.cookie)
  return dedupe([
    ...(cookieLocale ? [cookieLocale.localeCode] : []),
    ...parseAcceptLanguage(headers?.['accept-language']),
  ])
}

const fromBrowser = (win: Window): string[] => {
  const cookieLocale = readLocaleCookie(win.document?.cookie)
  const navigatorLanguages = win.navigator?.languages?.length
    ? [...win.navigator.languages]
    : win.navigator?.language
      ? [win.navigator.language]
      : []
  return dedupe([
    ...(cookieLocale ? [cookieLocale.localeCode] : []),
    ...navigatorLanguages,
  ])
}

// A real Window exposes both navigator and document; a Next.js context has
// neither. We avoid the canonical self-reference check (`value.window ===
// value`) so plain test fixtures don't need a circular reference.
const isWindow = (value: unknown): value is Window =>
  typeof value === 'object' &&
  value !== null &&
  'navigator' in value &&
  'document' in value

/**
 * The viewer's language preference for a session, most-preferred first, for
 * feeding into getMarketLocale. The locale cookie (the user's established,
 * cross-subdomain choice) ranks above the browser/Accept-Language signals.
 *
 * Works on both sides: pass the Next.js server context server-side, or a
 * Window (or nothing, to use the global window) client-side.
 *
 * @param source - a Next.js server context (server-side) or a Window
 *   (client-side; omit to use the global window). A NextPageContext passed on
 *   the client (e.g. from getInitialProps) has no `req`, so the context is
 *   ignored and the global window is used instead — the correct client-side
 *   behaviour.
 */
export function getPreferredLanguages(
  source?: LocalePreferenceSource,
): string[] {
  // A server context carries the request: read its cookie + Accept-Language.
  const ctx = source as
    | Partial<GetServerSidePropsContext & NextPageContext>
    | undefined
  if (ctx?.req) {
    return fromHeaders(ctx.req.headers)
  }

  // Otherwise resolve from the browser: an explicit Window, else the global.
  const win = isWindow(source)
    ? source
    : typeof window !== 'undefined'
      ? window
      : undefined
  return win ? fromBrowser(win) : []
}

/**
 * Resolves the most relevant live locale for a market for the current session
 * — the convenience entry point consumers usually want. Combines
 * getPreferredLanguages (locale cookie + browser/Accept-Language) with the
 * getMarketLocale matcher, so callers don't assemble a preference list
 * themselves: `getMarketLocaleForSession('ESP', ctx)` server-side, or
 * `getMarketLocaleForSession('ESP')` client-side.
 *
 * Returns undefined when the market is unknown or has no live locale.
 */
export function getMarketLocaleForSession(
  countryCode: string,
  source?: LocalePreferenceSource,
): LiveLanguageCodes | undefined {
  return getMarketLocale(countryCode, getPreferredLanguages(source))
}
